// ============================================================================
// GESTION SOCIALE — le questionnaire « Fiche d'identification du salarié »
// ============================================================================
// ⚠️ Miroir du schéma backend (`officina-backend/src/services/social-form.ts`,
// `ficheSalarieSchema`). Une question ajoutée ici doit l'être là-bas — sinon
// le serveur la jette sans bruit à l'enregistrement.
//
// Les cases à cocher du papier sont des choix UNIQUES : '' = rien de coché.
// ============================================================================

export const JOURS = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'] as const;
export type Jour = (typeof JOURS)[number];

export interface Creneau {
  heures: string;
  horaires: string;
}

export interface PeriodeAnterieure {
  du: string;
  au: string;
  type: string;
  precision: string;
}

export interface FicheSalarie {
  entreprise: string;

  nom: string;
  prenom: string;
  nomJeuneFille: string;
  dateNaissance: string;
  lieuNaissance: string;
  nationalite: string;
  nationaliteAutre: string;
  titreSejour: string;
  numeroSecu: string;
  mail: string;
  situation: string;
  situationAutre: string;
  adresse: string;
  codePostal: string;
  ville: string;
  dejaEmploye: string;
  periodes: PeriodeAnterieure[];
  lienParente: string;
  lienParentePrecision: string;
  actionnaire: string;
  situationAnterieure: string;
  situationAnterieureAutre: string;
  dernierDiplome: string;
  cqp: string;
  experience: string;

  organisationPatronale: string;
  organisationPatronalePrecision: string;
  licenciementEco: string;
  licenciementEcoDates: string;
  conventionCollective: string;

  dateEmbauche: string;
  typeContrat: string;
  cddTerme: string;
  cddDateFin: string;
  cddDureeMinimale: string;
  cddTermeTexte: string;
  cddMotif: string;
  remplacementType: string;
  remplacementNom: string;
  remplacementMotif: string;
  remplacementMotifAutre: string;
  accroissementMotif: string;
  saison: string;
  cddAutre: string;
  periodeEssai: string;
  essaiDuree: string;
  essaiDureeAutre: string;
  essaiRenouvellement: string;

  intitulePoste: string;
  niveau: string;
  echelon: string;
  coefficient: string;
  statut: string;
  categorie: string;
  taches: string;
  lieuTravail: string;
  deplacements: string;
  deplacementsLimites: string;
  deplacementsLesquelles: string;

  dureeHebdo: string;
  dureeHebdoAutre: string;
  dimanches: string;
  nuits: string;
  feries: string;
  horaire: string;
  joursSemaine: string;
  joursSemaineAutre: string;
  amenagement: string;
  amenagementPrecision: string;
  forfait: string;
  repartitionSemaine: Record<Jour, Creneau>;
  repartitionMois: Creneau[];
  modificationRepartition: string;
  heuresComplementaires: string;
  repartitionAnnee: string;

  salaireBrut: string;
  primes: string;
  primesPrecision: string;
  nourriture: string;
  logement: string;
  vehiculeFonction: string;
  vehiculeService: string;
  ordinateur: string;
  telephone: string;
  avantageAutre: string;
  fraisPro: string;
  fraisProLesquels: string;
  abattement: string;
  congesRegime: string;
  congesDecompte: string;
  clauses: string;
  clausesPrecision: string;
  contratParticulier: string;
  contratParticulierAutre: string;

  signataire: string;
  dateSignature: string;
  /** PNG en data URL, tracé à l'écran. */
  signature: string;
}

/** Clés dont la valeur est une simple chaîne (tout sauf les tableaux/objets). */
export type ChampTexte = {
  [K in keyof FicheSalarie]: FicheSalarie[K] extends string ? K : never;
}[keyof FicheSalarie];

/**
 * Le serveur renvoie toujours une fiche complète (il re-parse avec ses valeurs
 * par défaut) ; ceci ne fait que garantir les tableaux à leur longueur papier :
 * deux périodes antérieures, quatre semaines.
 */
export function normaliser(d: FicheSalarie): FicheSalarie {
  const vide = { heures: '', horaires: '' };
  return {
    ...d,
    periodes: [0, 1].map((i) => d.periodes?.[i] ?? { du: '', au: '', type: '', precision: '' }),
    repartitionMois: [0, 1, 2, 3].map((i) => d.repartitionMois?.[i] ?? { ...vide }),
    repartitionSemaine: Object.fromEntries(
      JOURS.map((j) => [j, d.repartitionSemaine?.[j] ?? { ...vide }]),
    ) as Record<Jour, Creneau>,
  };
}

/** Ce qu'il faut au minimum pour envoyer : de quoi nommer le salarié. */
export function manquants(d: FicheSalarie): string[] {
  const m: string[] = [];
  if (!d.nom.trim()) m.push('Nom');
  if (!d.prenom.trim()) m.push('Prénom');
  return m;
}

/** Utile, pas bloquant : on le signale avant l'envoi. */
export function aVerifier(d: FicheSalarie): string[] {
  const m: string[] = [];
  if (!d.dateEmbauche) m.push("Date d'embauche");
  if (!d.typeContrat) m.push('Type de contrat (CDI / CDD)');
  if (!d.numeroSecu.trim()) m.push('N° de sécurité sociale');
  if (!d.intitulePoste.trim()) m.push('Intitulé du poste');
  if (!d.salaireBrut.trim()) m.push('Salaire brut mensuel');
  if (!d.signature) m.push('Signature');
  return m;
}
